
import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

export const TermsOfService = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black text-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-400">Last updated: January 2026</p>
        </div>

        <Card className="bg-gray-800/50 border-gray-700 mb-8">
          <CardContent className="p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                By accessing and using the services of Centre Stage Sports & Celebrities ("Company," "we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Services Overview</h2>
              <p className="text-gray-300 mb-4">We provide the following services:</p>
              <ul className="text-gray-300 space-y-2 list-disc list-inside">
                <li><strong>Celebrity Management:</strong> Talent booking, brand endorsements, and celebrity partnerships</li>
                <li><strong>Event Management:</strong> Corporate events, product launches, and entertainment shows</li>
                <li><strong>Sponsorship Management:</strong> Brand sponsorship opportunities and partnership facilitation</li>
                <li><strong>Merchandising & Gifting:</strong> Branded merchandise and promotional products</li>
                <li><strong>Sporting Experiences:</strong> VIP sports experiences and hospitality packages</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Client Responsibilities</h2>
              <ul className="text-gray-300 space-y-2 list-disc list-inside">
                <li>Provide accurate and complete information for service delivery</li>
                <li>Make timely payments as per agreed terms</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Respect intellectual property rights of celebrities and partners</li>
                <li>Provide necessary approvals and clearances for events</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Payment Terms</h2>
              <ul className="text-gray-300 space-y-2 list-disc list-inside">
                <li>Payment terms are specified in individual service agreements</li>
                <li>Advance payments may be required for celebrity bookings and events</li>
                <li>Late payment charges may apply as per agreed terms</li>
                <li>All payments are subject to applicable taxes</li>
                <li>Refunds are subject to specific terms in service agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Cancellation and Refunds</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-amber-400 mb-2">Cancellation by Client</h3>
                  <ul className="text-gray-300 space-y-2 list-disc list-inside">
                    <li>Cancellation terms vary by service type and timing</li>
                    <li>Celebrity bookings may have specific cancellation clauses</li>
                    <li>Event cancellations are subject to vendor commitments</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-amber-400 mb-2">Force Majeure</h3>
                  <p className="text-gray-300">
                    Neither party shall be liable for delays or failures due to circumstances beyond reasonable control, including but not limited to natural disasters, government restrictions, or celebrity unavailability due to health reasons.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property</h2>
              <ul className="text-gray-300 space-y-2 list-disc list-inside">
                <li>All content and materials remain the property of their respective owners</li>
                <li>Celebrity image rights and likeness are protected by law</li>
                <li>Clients must obtain proper licensing for any recorded content</li>
                <li>Company logos and branding materials are proprietary</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Confidentiality</h2>
              <p className="text-gray-300">
                Both parties agree to maintain confidentiality of sensitive information shared during the course of business, including but not limited to celebrity fees, event details, and business strategies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-300">
                Our liability is limited to the value of services provided. We are not liable for indirect, consequential, or punitive damages. This limitation applies to the fullest extent permitted by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Indemnification</h2>
              <p className="text-gray-300">
                Clients agree to indemnify and hold harmless Centre Stage Sports & Celebrities from any claims, damages, or expenses arising from their use of our services or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Governing Law</h2>
              <p className="text-gray-300">
                These terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the jurisdiction of courts in Mumbai, Maharashtra.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Modification of Terms</h2>
              <p className="text-gray-300">
                We reserve the right to modify these terms at any time. Changes will be effective upon posting on our website. Continued use of our services constitutes acceptance of modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Termination</h2>
              <p className="text-gray-300">
                Either party may terminate services with appropriate notice as specified in individual agreements. Termination does not relieve parties of obligations incurred prior to termination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Information</h2>
              <p className="text-gray-300 mb-2">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="text-gray-300 space-y-2">
                <p>Email: connect@centrestageent.com</p>
                <p>Phone: +91 98101 72766</p>
                <p>Address: 101 Saroj Building, VP Road, Vile Parle, Mumbai 56</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Severability</h2>
              <p className="text-gray-300">
                If any provision of these terms is found to be unenforceable, the remaining provisions will continue to be valid and enforceable.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
